import type { ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@/fontawesome.ts';

export interface MultiCreateField<T> {
  key: string;
  header: string;
  align?: 'left' | 'center';
  /** Extra classes for the desktop `<td>` (column widths etc.). */
  cellClassName?: string;
  /** Rendered as the card headline on mobile, without its label. */
  primary?: boolean;
  /** Rendered unlabelled in the top right of the mobile card - for status icons and the like. */
  mobileTrailing?: boolean;
  /** Takes only half a row on the mobile card - for narrow inputs. */
  mobileHalf?: boolean;
  /** Puts the control next to its label instead of below it (checkboxes); implies `mobileHalf`. */
  mobileInline?: boolean;
  render: (row: T) => ReactNode;
}

interface MultiCreateTableProps<T> {
  fields: MultiCreateField<T>[];
  rows: T[];
  keyExtractor: (row: T) => string;
  /** Omit for tables whose rows cannot be removed - no delete column is rendered then. */
  onRemove?: (row: T) => void;
  /** Disables every delete button (while submitting); the last remaining row is always locked. */
  removeDisabled?: boolean;
  /** Headline above the primary field of a mobile card, e.g. `(i) => \`Trade ${i + 1}\``. */
  rowLabel?: (index: number) => string;
}

export default function MultiCreateTable<T>({
  fields,
  rows,
  keyExtractor,
  onRemove,
  removeDisabled = false,
  rowLabel,
}: MultiCreateTableProps<T>) {
  const primaryIndex = Math.max(fields.findIndex(f => f.primary === true), 0);
  const primaryField = fields[primaryIndex];
  const trailingField = fields.find(f => f.mobileTrailing === true);
  const detailFields = fields.filter((field, idx) => idx !== primaryIndex && field.mobileTrailing !== true);

  const removeButton = (row: T, className: string) => {
    if (onRemove === undefined) {
      return null;
    }
    return (
      <button
        type="button"
        onClick={() => onRemove(row)}
        disabled={rows.length === 1 || removeDisabled}
        className={`text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed ${className}`}
        aria-label="Zeile entfernen"
      >
        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
      </button>
    );
  };

  return (
    <>
      {/* Desktop: the regular table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              {fields.map(field => (
                <th
                  key={field.key}
                  scope="col"
                  className={`px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider ${
                    field.align === 'center' ? 'text-center' : 'text-left'
                  }`}
                >
                  {field.header}
                </th>
              ))}
              {onRemove !== undefined && <th className="px-3 py-2" />}
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {rows.map(row => (
              <tr key={keyExtractor(row)}>
                {fields.map(field => (
                  <td
                    key={field.key}
                    className={`px-3 py-2 ${field.align === 'center' ? 'text-center' : ''} ${field.cellClassName ?? ''}`}
                  >
                    {field.render(row)}
                  </td>
                ))}
                {onRemove !== undefined && <td className="px-3 py-2">{removeButton(row, '')}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile: one card per row with the fields stacked instead of a sideways scrolling table */}
      <div className="md:hidden space-y-3">
        {rows.map((row, index) => (
          <div
            key={keyExtractor(row)}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1">
                {rowLabel !== undefined && (
                  <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                    {rowLabel(index)}
                  </span>
                )}
                {primaryField.render(row)}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {trailingField !== undefined && trailingField.render(row)}
                {removeButton(row, 'p-2 -mr-1 -mt-1')}
              </div>
            </div>

            {detailFields.length > 0 && (
              <div className="mt-3 grid grid-cols-2 gap-3">
                {detailFields.map(field => {
                  const inline = field.mobileInline === true;
                  const span = inline || field.mobileHalf === true ? 'col-span-1' : 'col-span-2';
                  return (
                    <label key={field.key} className={`min-w-0 ${span} ${inline ? 'flex items-center gap-2' : 'block'}`}>
                      {inline && field.render(row)}
                      <span className={`text-xs font-medium text-gray-500 dark:text-gray-400 ${inline ? '' : 'block mb-1'}`}>
                        {field.header}
                      </span>
                      {!inline && field.render(row)}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
